import type { IDealerInstalledOptions, OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import { getCurrentOfferType, getDealerSettings, getOfferDetails, getVehicleDetails } from './offerNodeSelectors';
import { getNonConditionalTotalByOfferType } from './incentiveSelectors';
import { getDiscount } from './selectedTermSelectors';
import { MY_OFFER_PRICE_LABEL, NEGOTIABLE_LABEL, SINGLE_PRICE_LABEL } from '../constants/meta';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

export function getVehicleSinglePriceData(state: OfferReduxBaseState) {
    return {
        dealerSettings: getDealerSettings(state),
        vehicle: getVehicleDetails(state)
    };
}

export function isVehicleSinglePrice(state: OfferReduxBaseState) {
    const { dealerSettings, vehicle } = getVehicleSinglePriceData(state);
    // TODO: vehicle.condition is deprecated. vehicle.type should be used.
    const vehicleType = vehicle.type || vehicle.condition;
    return vehicleType === 'new' ? dealerSettings.isNewSinglePrice : dealerSettings.isUsedSinglePrice;
}

export function getFinanceOfferPriceLabel(state: OfferReduxBaseState, enableP202Redesign = false) {
    const isSinglePriceVehicle = isVehicleSinglePrice(state);
    const isCashOfferType = getCurrentOfferType(state) === CASH;
    if (enableP202Redesign && isCashOfferType && !isSinglePriceVehicle) {
        return MY_OFFER_PRICE_LABEL;
    }
    return isSinglePriceVehicle ? SINGLE_PRICE_LABEL : NEGOTIABLE_LABEL;
}

export function getLeaseOfferPriceLabel(state: OfferReduxBaseState) {
    const isSinglePriceVehicle = isVehicleSinglePrice(state);
    return isSinglePriceVehicle ? SINGLE_PRICE_LABEL : NEGOTIABLE_LABEL;
    // TODO: Show MSRP if isSinglePrice and dealer bases lease payments on MSRP
}

export function shouldCashAskingPriceLabelAndInputDisplay(state: OfferReduxBaseState, enableP202Redesign = false) {
    if (enableP202Redesign) {
        const isCashOfferType = getCurrentOfferType(state) === CASH;
        const isSinglePriceVehicle = isVehicleSinglePrice(state);
        return isCashOfferType ? !isSinglePriceVehicle : true;
    } else {
        return true;
    }
}

/**
 * Get the retail price override by offer type.
 * @param state redux state
 * @param offerType offer type
 * @param enableFallback (default: false) Pass true to enable falling back to the vehicle MSRP
 * @returns MSRP Value
 */
export const getRetailPriceOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    enableFallback = false
): number | undefined => {
    const { retailPriceOverride } = getOfferDetails(state, offerType);
    if (retailPriceOverride == null || retailPriceOverride === undefined) {
        // NOTE: null/undefined check on purpose
        if (!enableFallback) return undefined;

        return getVehicleDetails(state)?.retailPrice || 0; // fallback to zero
    }
    return retailPriceOverride;
};

/**
 * Get the retail price override
 * @param state redux state
 * @param enableFallback (default: false) Pass true to enable falling back to the vehicle MSRP
 * @returns MSRP value
 */
export const getRetailPriceOverride = (state: OfferReduxBaseState, enableFallback = false): number | undefined => {
    const offerType = getCurrentOfferType(state);
    return getRetailPriceOverrideByOfferType(state, offerType, enableFallback);
};

export function getYourPriceValue(state: OfferReduxBaseState, offerType: OfferType): number {
    const nonIncentiveValue = getNonConditionalTotalByOfferType(state, offerType);
    const { offerPrice } = getOfferDetails(state, offerType);
    const result = offerPrice! - nonIncentiveValue;
    return result;
}

export function getAnalyticsPayloadVehicleDetails(state: OfferReduxBaseState) {
    const offerVehicle = getVehicleDetails(state);
    const rootVehicle = state?.vehicle;
    return { ...offerVehicle, ...rootVehicle };
}

export function getDealerInstalledOptionsAMD(state: OfferReduxBaseState): IDealerInstalledOptions[] {
    const rootVehicle = state?.vehicle;
    const offerVehicle = getVehicleDetails(state);
    return rootVehicle?.dealerInstalledOptions || offerVehicle?.dealerInstalledOptions || [];
}

export function getDIOTotalValues(state: OfferReduxBaseState): number {
    const installedOptions = getDealerInstalledOptionsAMD(state);
    if (installedOptions.length === 0) return 0;
    return installedOptions.reduce((accumulator, currentDIO) => accumulator + currentDIO.value, 0);
}

export function getResidualDiscount(state: OfferReduxBaseState): number {
    const diosValue = getDIOTotalValues(state);
    return getDiscount(state) + diosValue;
}
